<script setup>
import { defineProps, defineEmits, computed } from 'vue'
import CartPaymentOrderSummaryAccordion from '../CartPaymentOrderSummaryAccordion.vue'

const props = defineProps({
  companyApplicant: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['edit', 'delete'])

const editCompanyApplicant = () => {
  emit('edit', props.companyApplicant.id)
}

const deleteCompanyApplicant = () => {
  emit('delete', props.companyApplicant.id)
}

const isNameComplete = computed(
  () => !!props.companyApplicant.firstName && !!props.companyApplicant.lastName
)
const isDateOfBirthComplete = computed(
  () => !!props.companyApplicant.dateOfBirth
)
const isIdComplete = computed(() => !!props.companyApplicant.idImage)
const isAddressComplete = computed(
  () =>
    !!props.companyApplicant.addressLine1?.trim() &&
    !!props.companyApplicant.addressCity?.trim() &&
    !!props.companyApplicant.addressState?.trim() &&
    !!props.companyApplicant.addressZip?.trim()
)

const isFincenIdComplete = computed(
  () => !!props.companyApplicant.fincenIdentifier
)

const isComplete = computed(
  () =>
    (isNameComplete.value &&
      isDateOfBirthComplete.value &&
      isIdComplete.value &&
      isAddressComplete.value) ||
    (props.companyApplicant.fincenIdentifierType === 'fincen_id' &&
      isFincenIdComplete.value)
)

// Need to format the date of birth into utc sine that what the server is storing in
const formatDate = (date) => {
  if (!date || !(date instanceof Date)) return null

  // Extract the UTC components of the date
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()

  // Create a new Date object using these components
  const utcDate = new Date(Date.UTC(year, month, day))

  // Format the date
  return utcDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC' // Ensure consistent output regardless of local time zone
  })
}

const idTypeDisplay = computed(() => {
  if (props.companyApplicant.idType === 'us_passport') {
    return 'US Passport'
  } else if (props.companyApplicant.idType === 'state_drivers_license') {
    return "State Driver's License"
  } else if (props.companyApplicant.idType === 'state_id_card') {
    return 'State Issued ID Card'
  } else if (props.companyApplicant.idType === 'foreign_passport') {
    return 'Government ID'
  } else {
    return 'Unknown ID Type'
  }
})

const cardTitle = computed(() => {
  if (props.companyApplicant.fincenIdentifierType === 'fincen_id') {
    return `FinCEN ID: ${props.companyApplicant.fincenIdentifier}`
  } else {
    return `${props.companyApplicant.firstName} ${props.companyApplicant.lastName}`
  }
})
</script>

<template>
  <div class="card border-1 shadow-none mb-3" v-if="!companyApplicant.deleted">
    <div class="card-body p-4">
      <div class="d-block align-items-center mb-3">
        <h4 class="card-title mb-1">
          {{ cardTitle }}
        </h4>
        <div>
          <span
            v-editable
            :class="[
              'badge',
              isComplete
                ? 'bg-soft-success text-success'
                : 'bg-soft-danger text-danger'
            ]"
          >
            <i
              :class="[
                'fa-light',
                isComplete ? 'fa-check-circle' : 'fa-exclamation-circle'
              ]"
            ></i>
            {{ isComplete ? 'Ready to Submit' : 'Incomplete' }}
          </span>
        </div>
      </div>

      <div
        class="row"
        v-if="companyApplicant.fincenIdentifierType !== 'fincen_id'"
      >
        <div class="col-md-6">
          <dl>
            <dt>Name</dt>
            <dd>
              {{ companyApplicant.firstName }} {{ companyApplicant.lastName }}
            </dd>

            <dt>Date of Birth</dt>
            <dd>
              <template v-if="isDateOfBirthComplete">
                {{ formatDate(companyApplicant.dateOfBirth) }}
              </template>
              <span v-else class="badge bg-soft-danger text-danger">
                <i class="fa-light fa-exclamation-circle"></i>
                Date of birth is missing
              </span>
            </dd>
          </dl>
        </div>
        <div class="col-md-6">
          <dl>
            <dt>Government ID</dt>
            <dd>
              <template v-if="isIdComplete">{{ idTypeDisplay }}</template>
              <span v-else class="badge bg-soft-danger text-danger">
                <i class="fa-light fa-exclamation-circle"></i>
                Government ID is missing
              </span>
            </dd>

            <dt>Address</dt>
            <dd>
              <template v-if="isAddressComplete">
                {{ companyApplicant.addressLine1 }},
                <template v-if="companyApplicant.addressLine2"
                  >{{ companyApplicant.addressLine2 }},</template
                ><br />
                {{ companyApplicant.addressCity }},
                {{ companyApplicant.addressState }}
                {{ companyApplicant.addressZip }}
              </template>
              <span v-else class="badge bg-soft-danger text-danger">
                <i class="fa-light fa-exclamation-circle"></i>
                Missing Address
              </span>
            </dd>
          </dl>
        </div>
      </div>

      <div class="d-flex gap-2 mt-4">
        <button
          v-if="companyApplicant.fincenIdentifierType !== 'fincen_id'"
          v-editable
          @click="editCompanyApplicant"
          class="btn btn-outline-primary btn-sm"
          type="button"
        >
          <i class="fa-light fa-edit"></i> Edit
        </button>
        <button
          v-editable
          @click="deleteCompanyApplicant"
          class="btn btn-outline-danger btn-sm"
          type="button"
        >
          <i class="fa-light fa-times"></i> Remove
        </button>
      </div>
    </div>
  </div>
</template>
